import React from 'react';
import { useParams } from 'react-router-dom';
import { Checkbox } from '@progress/kendo-react-inputs';

import { ArgumentSelector } from './argument-selector';
import SelectionItem, { ArgumentItem } from '../../types/selection-item';
import {
  GetZones,
  GetTasks,
  GetTimers,
  GetAreas,
  GetTitles,
  GetItems,
  GetTasksHasBoundary,
  GetTaskById,
  GetTaskContentById
} from '../../utils/game-document/assets/index';
import DropdownSelector from './dropdown-selector';
import { GameDocument } from '../../types/game-document';
import { GetResourceById, GetResourceValue } from '../../utils/game-document';
import { GameDocumentContext } from '../../contexts/game-document';

export interface OperationSelectorProps {
  operation?: string;
  taskId?: string;
  gameDocument?: GameDocument;
  argumentQuantity?: number;
  useFallbackValue?: boolean;
  argumentBoolean?: boolean;
  operationsList: SelectionItem[];
  argumentAssetId?: string | string[] | undefined;
  onChangeOperation?: (operation: string) => void;
  onChangeQuantity?: (quantity: number) => void;
  onChangeCheckbox?: (isChecked: boolean) => void;
  onChangeArgument?: (argument: string | string[]) => void;
}

export const GetArgumentList = (
  listName: string | undefined,
  gameDocument: GameDocument | undefined,
  taskId?: string
): ArgumentItem[] | undefined => {
  switch (listName) {
    case 'zone':
      return GetZones(gameDocument).map((z) => ({
        id: z.id,
        title: GetResourceValue(gameDocument!, z.titleResId ?? ''),
        description: z.description
      }));
    case 'task':
      return GetTasks(gameDocument).map((z) => ({
        id: z.id,
        title: GetResourceValue(gameDocument!, z.titleResId ?? ''),
        description: z.description
      }));
    case 'task-has-boundary':
      return GetTasksHasBoundary(gameDocument).map((z) => ({
        id: z.id,
        title: GetResourceValue(gameDocument!, z.titleResId ?? ''),
        description: z.description
      }));
    case 'area':
      return GetAreas(gameDocument).map((z) => ({
        id: z.id,
        title: GetResourceValue(gameDocument!, z.titleResId ?? ''),
        description: z.description
      }));
    case 'item':
      return GetItems(gameDocument).map((z) => ({
        id: z.id,
        title: GetResourceValue(gameDocument!, z.titleResId ?? ''),
        description: z.description
      }));
    case 'bank-item':
      return GetItems(gameDocument)
        .filter((item) => item.useBank)
        .map((z) => ({
          id: z.id,
          title: GetResourceValue(gameDocument!, z.titleResId ?? ''),
          description: z.description
        }));
    case 'title':
      return GetTitles(gameDocument).map((z) => ({
        id: z.id,
        title: GetResourceValue(gameDocument!, z.titleResId ?? ''),
        description: z.description
      }));
    case 'timer':
      return GetTimers(gameDocument).map((z) => ({
        id: z.id,
        title: GetResourceValue(gameDocument!, z.titleResId ?? ''),
        description: z.description
      }));
    case 'question':
      if (!gameDocument || !taskId) return [];

      const selectedTask = GetTaskById(gameDocument, taskId);
      if (!selectedTask || !selectedTask.taskContentId) return [];

      const selectedTaskContent = GetTaskContentById(
        gameDocument,
        selectedTask?.taskContentId ?? ''
      );
      if (!selectedTaskContent || !selectedTaskContent.forms) return [];

      const questions = selectedTaskContent.forms
        .filter((f) => ['radio', 'checkbox'].includes(f.type))
        .filter((f) => f.mediaOnlyIdentifierResId)
        .map((f) => ({
          id: f.id ?? '',
          title:
            GetResourceById(gameDocument!, f.mediaOnlyIdentifierResId ?? '')
              ?.value ?? ''
        }));

      return questions;
    default:
      return undefined;
  }
};

const OperationSelector = ({ ...props }: OperationSelectorProps) => {
  const [state] = React.useContext(GameDocumentContext);
  const [selectedArgument, setSelectedArgument] = React.useState<
    ArgumentItem | ArgumentItem[] | undefined
  >();

  const selectedOperation = React.useMemo(
    () =>
      props.operationsList.find(
        (op) => op.name === props.operation
      ) as SelectionItem,
    [props.operationsList, props.operation]
  );

  const onClick = (item: SelectionItem) => {
    if (props.onChangeOperation) {
      props.onChangeOperation(item.name);
    }
  };

  const onChange = (value: number) => {
    if (props.onChangeQuantity) {
      props.onChangeQuantity(value);
    }
  };

  const onChangeCheckbox = (isChecked: boolean) => {
    if (props.onChangeCheckbox) {
      props.onChangeCheckbox(isChecked);
    }
  };

  const onArgumentSelected = (
    selectedArgument: SelectionItem | SelectionItem[]
  ) => {
    if (props.onChangeArgument) {
      if (selectedOperation.name === 'choosesAnswer') {
        props.onChangeArgument([(selectedArgument as SelectionItem).id]);
        return;
      }

      if (selectedOperation?.isMultipleArgument) {
        const ids = (selectedArgument as SelectionItem[]).map((x) => x.id);
        props.onChangeArgument(ids);
      }

      if (!selectedOperation?.isMultipleArgument) {
        props.onChangeArgument((selectedArgument as SelectionItem).id);
      }
    }
  };

  const argumentList = GetArgumentList(
    selectedOperation?.list,
    state?.gameDocument,
    props.taskId ?? ''
  );

  const argumentLabel = React.useMemo(() => {
    if (!selectedOperation) return '';
    const name = selectedOperation.name.toLowerCase();
    if (name.includes('zone')) return 'Zone';
    if (name.includes('area')) return 'Area';
    if (name.includes('task')) return 'Task';
    if (name.includes('item') || name.includes('stock')) return 'Item';
    if (name.includes('title') || name.includes('role')) return 'Title';
    if (name.includes('timer')) return 'Timer';
    if (name.includes('answer')) return 'Question';
    return '';
  }, [selectedOperation]);

  React.useEffect(() => {
    if (props.operationsList.length > 0) {
      props.operationsList.sort((a, b) => {
        if (a.description && b.description) {
          if (a.description < b.description) return -1;
          if (a.description > b.description) return 1;
        }
        return 0;
      });
    }
  }, [props.operationsList]);

  React.useEffect(() => {
    if (selectedOperation) {
      if (selectedOperation?.isMultipleArgument) {
        setSelectedArgument(
          argumentList?.filter((i) =>
            props.argumentAssetId?.includes(i.id)
          ) as any
        );
      } else {
        setSelectedArgument(
          argumentList?.find((i) =>
            props.argumentAssetId?.includes(i.id)
          ) as any
        );
      }
    }
  }, [props.argumentAssetId, selectedOperation]);

  return (
    <>
      <div className={'d-flex flex-column'}>
        <span>What</span>
        <DropdownSelector<SelectionItem, 'description'>
          fieldName={'description'}
          placeholder={'action'}
          onClick={(e) => onClick(e)}
          items={props.operationsList}
          additionalClassname={`operation--${selectedOperation?.name ?? 'select'}`}
          selected={selectedOperation?.description}
        />
      </div>

      {selectedOperation?.isMultipleArgument &&
        !['openRandomTask', 'randomTask', 'assignTitle'].includes(
          selectedOperation.name
        ) && (
          <code className={'mb-1'}>
            {`Select (${(selectedArgument as ArgumentItem[])?.length ?? 0})`}
          </code>
        )}

      {argumentList &&
        !selectedOperation?.isMultipleArgument &&
        !selectedOperation?.noActionShowed && (
          <div className={'d-flex flex-column'}>
            <span>{argumentLabel}</span>
            <ArgumentSelector
              argumentList={argumentList}
              onArgumentSelected={onArgumentSelected}
              selectedArgument={selectedArgument as ArgumentItem}
            />
          </div>
        )}

      {selectedOperation?.showQuantity && (
        <div className={'d-flex flex-column'}>
          <span>Quantity</span>
          <input
            min={0}
            type={'number'}
            style={{
              width: '130px',
              height: '30px'
            }}
            value={props.argumentQuantity}
            className={'form-control form-event-control-inline'}
            onChange={(e) => onChange(parseInt(e.target.value))}
            disabled={
              props.operation === 'giveScore(score)' && props.useFallbackValue
            }
          />
        </div>
      )}

      {selectedOperation?.checkboxAction && (
        <div className={'mb-1'}>
          <Checkbox
            name={selectedOperation?.name}
            onChange={(e) => onChangeCheckbox(e.value)}
            label={selectedOperation?.checkboxAction.label}
            value={props.useFallbackValue || props.argumentBoolean}
          />
        </div>
      )}
    </>
  );
};

export default OperationSelector;
